import {
  CHECKOUT_RULE_DOCUMENT_TYPES,
  CheckoutRuleDocumentType,
} from '../interfaces'
import type { SessionCheckoutRule } from '../graphQL/queries/getSession/types'
import type { AutoSettleStatus } from '../redux/slices/checkout/types'
import type { RootState } from '../redux/store'
import { ESCAPES_ORIGIN, resolveCheckoutRuleRoute } from './checkoutRuleRoute'

/**
 * Has the shopper chosen a value for this checkout rule?
 *
 * One module, because two subsystems need the same answer and must not drift:
 * `utils/cartCheckoutPanel.ts` renders the pick on the cart's Entrega/Pago
 * rows, and COM-1046's summary CTA decides between *Continuar* and *Realizar
 * pedido* on it. COM-1048 reads it again after auto-settling a one-option rule.
 *
 * **Opening a picker is not choosing** (Product, 2026-09-23). A shopper who
 * opens the calendar and backs out has settled nothing, and the CTA keeps them
 * there. So nothing here consults a screen visit, and there is no `visited` map
 * to consult.
 *
 * Two places hold a pick and this module reads both:
 *
 * - `checkoutSlice.selections[ruleType]` — this session's pick, written by the
 *   picker screens through `useSetCheckoutRuleSelection` and **only** on the
 *   mutation's success. Lost on reload.
 * - `sessionData.checkoutRules` — the persisted pick the boot response carries.
 *   Survives reload; goes stale the moment a write lands, because nothing
 *   updates it in place.
 *
 * The fresh pick therefore wins and the persisted one backs it. That ordering
 * is what makes a reload safe *and* keeps the cart row from showing yesterday's
 * date for the rest of the session.
 */

/**
 * The persisted pick for one rule type, or `undefined`.
 *
 * The single reader of `session.checkoutRules`. `utils/cartCheckoutPanel.ts`
 * called a private `findData` for this until COM-1045; it now calls here, so
 * the panel and the settled-selector cannot disagree about what the array says.
 *
 * ⚠️ **An entry is not a pick.** `value` is a nullable
 * `SessionCheckoutRuleValue` and its `data` is `string | null | undefined`, so
 * a rule the storefront wrote with nothing chosen arrives as
 * `{ ruleType, value: null }` or `{ ruleType, value: { data: null } }`. Read
 * the chain as `?.value?.data`; `.value.data` throws on the first of those.
 *
 * `setCheckoutRuleById` filters and appends server-side, so the array holds at
 * most one entry per `ruleType`. `find` reflects that. A second entry for the
 * same type is ignored rather than rejected — cinnamon has no authority to
 * refuse a shape the server accepted, and picking between two would be a coin
 * toss.
 *
 * `ruleType` on the wire is `String!` with no enum behind it, so the array can
 * carry a type this build has never heard of. Comparing by value is safe
 * because `SessionRuleTypeMatchesConfig` in `getSession/types.ts` fails the
 * build if `SessionCheckoutRuleType` and `CheckoutRules` ever drift apart.
 */
export const readSessionRulePick = (
  rules: SessionCheckoutRule[] | null | undefined,
  ruleType: CheckoutRuleDocumentType,
): string | undefined => {
  if (!Array.isArray(rules) || rules.length === 0) return undefined

  const match = rules.find((rule) => rule?.ruleType === ruleType)
  const data = match?.value?.data

  return typeof data === 'string' && data !== '' ? data : undefined
}

/**
 * The shopper's pick for one rule type: this session's first, the persisted one
 * behind it.
 *
 * **The precedence is load-bearing in both directions.** Reading the session
 * copy first would show a stale value after every write, because
 * `sessionData` is boot-time data that `setCheckoutRuleSession` does not
 * update in place (COM-1066 research, 2026-09-28). Reading only the fresh copy
 * would walk a returning shopper through pickers they have already answered.
 *
 * Both sides are normalised to "a non-empty string, or nothing". The two types
 * disagree — `ICheckoutRuleValue.data` is a required `string` while the
 * session's is `string | null | undefined` — and without the normalisation an
 * empty string would read as settled on one side and unsettled on the other
 * for the same shopper.
 */
export const selectRulePick = (
  state: RootState,
  ruleType: CheckoutRuleDocumentType,
): string | undefined => {
  /*
   * Optional all the way down, matching `isHidePricesEnabled` beside it.
   * `checkoutSlice` is registered on the real store and cannot be absent at
   * runtime, but a `RootState` in this repo is routinely a partial object cast
   * into shape by a test fixture, and a selector that throws on one is a
   * selector nobody can call from a suite that does not care about checkout.
   */
  const fresh = state?.checkoutSlice?.selections?.[ruleType]?.data
  if (typeof fresh === 'string' && fresh !== '') return fresh

  return readSessionRulePick(
    state?.defaultSlice?.sessionData?.checkoutRules,
    ruleType,
  )
}

/**
 * Whether the shopper has chosen a value for this rule.
 *
 * The one thing COM-1046's guided flow and COM-1048's auto-settle ask. Curried
 * so it reads as `useAppSelector(selectRuleIsSettled(ruleType))`, matching
 * `selectSelectionByRuleType` and `selectAvailableByRuleType` in
 * `redux/slices/checkout/checkout.ts`.
 *
 * It lives in `utils/` rather than beside those two because it spans slices —
 * the same reason `selectOrderDistributor` and `isHidePricesEnabled` do.
 *
 * ⚠️ **Settled says nothing about whether the rule was changeable.** A rule
 * with one option is settled by COM-1048 writing it, and a rule with no
 * `checkoutRuleRoutes` entry is settled by whatever the storefront persisted.
 * Ask `selectFirstHeldRule` below for changeability; asking this instead
 * strands a shopper on a CTA that goes nowhere.
 *
 * (This paragraph named a `resolveRuleChangeRoute` that never existed in any
 * build. COM-1046 wrote the function it was reaching for.)
 */
export const selectRuleIsSettled =
  (ruleType: CheckoutRuleDocumentType) =>
  (state: RootState): boolean =>
    selectRulePick(state, ruleType) !== undefined

/**
 * Auto-settle outcomes that leave the shopper a choice still to make.
 *
 * The set `selectAutoSettleStatus` prescribes for this ticket, and its
 * docstring carries the reasoning rather than repeating it here.
 *
 * ⚠️ **`undefined` is absent, and that omission is load-bearing.** It means
 * nothing decided — a rule the storefront does not serve, or a screen that
 * never mounted `useCheckoutRuleAutoSettle`. Reading it as "the shopper must
 * pick" holds the CTA at *Continuar* on a read-only storefront. The route test
 * below would catch that one anyway, so including it would be *safe* rather
 * than right; the contract says `undefined` is not `'multiple'`, and a quiet
 * deviation from it is the thing that bites the next reader.
 *
 * ⚠️ **`'empty'` and `'unavailable'` are absent too, and both are unsettled.**
 * A predicate written as "unsettled and routable" reads as equivalent and
 * bricks checkout: a rule offering nothing can never be chosen, so it would
 * hold the CTA for ever. That is the case COM-1048 exists to prevent, and it
 * is reachable on the pilot — `validateDates` drops the computed day unless it
 * falls strictly after today.
 */
const HOLDS_CTA: AutoSettleStatus[] = ['multiple', 'failed']

/** A rule still holding the CTA, and the screen that settles it. */
export interface HeldCheckoutRule {
  ruleType: CheckoutRuleDocumentType
  path: string
}

/**
 * The first rule still holding the CTA at *Continuar*, and where to send the
 * shopper to settle it — or `undefined` when they are done choosing.
 *
 * **One function answers two questions, which is the point.** The summary
 * CTA's label is `selectFirstHeldRule(state) !== undefined` and its tap target
 * is the same call's `path`. A separate predicate for the label would let a
 * button read *Continuar* and route nowhere.
 *
 * **Order comes from `CHECKOUT_RULE_DOCUMENT_TYPES`**, which is
 * `[DELIVERY_DATES, PAYMENT_METHODS]`. That array is what produces the
 * summary -> delivery -> payment -> summary walk; reordering it reorders the
 * walk. Same "first routable wins" posture as `useCheckoutRuleRedirect`.
 *
 * **The three tests run cheapest first** — a map read, then the settled
 * lookup, then `matchRoutes`. A storefront authoring no `checkoutRuleRoutes`
 * never reaches the third, because `useCheckoutRuleAutoSettle` leaves every
 * status `undefined` there. This runs on every store action while the summary
 * is mounted.
 *
 * ⚠️ **Reachability is not the auto-settle's `served`.** That flag tests key
 * presence on `checkoutRuleRoutes`; this tests `matchRoutes` against the
 * authored table. A storefront that authors the map entry and forgets the
 * route makes the first true and the second false, and gating on the first
 * would leave the CTA reading *Continuar* with nowhere to go, permanently.
 *
 * ⚠️ **`'failed'` skips the settled test on purpose.** A returning shopper
 * carries yesterday's pick on `session.checkoutRules`. The auto-settle fetches
 * today's single option, writes, is rejected, and rolls back to *nothing* in
 * the slice — so `selectRulePick` falls through to the session copy and the
 * rule reads settled on a value the storefront no longer offers. `'failed'` is
 * a positive claim that the stored value is wrong, so it outranks that.
 * The cost is the mirror case: the deadline fires during `'writing'`, the
 * mutation then lands, `advance('settled')` is refused as late, and the rule
 * reads settled on the *correct* value with `'failed'` beside it. That shopper
 * gets one picker visit they did not need. Walking someone to a screen is
 * recoverable; shipping a delivery date the storefront withdrew is not.
 *
 * `skip` excludes one rule type, and the picker screens' `confirm()` passes
 * their own. `confirm()` is ungated on a selection existing by design, so on a
 * storefront that never bound `checkout.ui.<RULE>.confirmDisabled` a shopper
 * can confirm having chosen nothing — and without `skip` they would be pushed
 * at the screen they are standing on, a button that visibly does nothing.
 *
 * The origin guard lives here rather than at each call site, so every caller
 * is safe by construction. See `ESCAPES_ORIGIN`.
 */
export const selectFirstHeldRule = (
  state: RootState,
  skip?: CheckoutRuleDocumentType,
): HeldCheckoutRule | undefined =>
  CHECKOUT_RULE_DOCUMENT_TYPES.reduce<HeldCheckoutRule | undefined>(
    (found, ruleType) => {
      if (found || ruleType === skip) return found

      /*
       * Optional all the way down, matching `selectRulePick` above and for its
       * reason: a `RootState` here is routinely a partial test fixture.
       */
      const status = state?.checkoutSlice?.autoSettle?.[ruleType]
      if (!status || !HOLDS_CTA.includes(status)) return undefined

      if (status !== 'failed' && selectRuleIsSettled(ruleType)(state)) {
        return undefined
      }

      const path = resolveCheckoutRuleRoute(
        state?.defaultSlice?.config?.checkoutRuleRoutes?.[ruleType],
        state?.defaultSlice?.config?.routes,
        state?.defaultSlice?.storeName,
        state?.defaultSlice?.sessionId,
      )
      if (!path || ESCAPES_ORIGIN.test(path)) return undefined

      return { ruleType, path }
    },
    undefined,
  )

export default selectRuleIsSettled
